import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { HomepageProvider } from "@/context/HomepageContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ErrorBoundary from "@/components/ErrorBoundary";
import posthog from "posthog-js";

// Initialize PostHog
if (process.env.REACT_APP_POSTHOG_KEY) {
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://eu.posthog.com",
    capture_pageview: false, // We'll manually track page views for SPA
    capture_pageleave: true,
    autocapture: true,
  });
}

// Pages
import HomePage from "@/pages/HomePage";
import ContactPage from "@/pages/ContactPage";
import ProductsPage from "@/pages/ProductsPage";
import PlatformPage from "@/pages/PlatformPage";
import AboutPage from "@/pages/AboutPage";
import InsightsPage from "@/pages/InsightsPage";
import EvaluatePage from "@/pages/EvaluatePage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import NewsPage from "@/pages/NewsPage";
import NewsArticlePage from "@/pages/NewsArticlePage";
import SecurityPage from "@/pages/SecurityPage";
import AdminPage from "@/pages/AdminPage";
import SapMigrationPage from "@/pages/SapMigrationPage";
import SapAssessmentPage from "@/pages/SapAssessmentPage";
import SapCalculatorPage from "@/pages/SapCalculatorPage";
import SapWhitepaperPage from "@/pages/SapWhitepaperPage";
import BusinessCasePage from "@/pages/BusinessCasePage";
import SettingsPage from "@/pages/SettingsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ImprintPage from "@/pages/ImprintPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import RegisterPage from "@/pages/RegisterPage";
import NotFoundPage from "@/pages/NotFoundPage";
import BenchmarksPage from "@/pages/BenchmarksPage";
import { SEOIndexPage, SEOCountryPage } from "@/pages/SEOLandingPages";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import VideoPage from "@/pages/VideoPage";
import PartnersPage from "@/pages/PartnersPage";
import IndustryPage from "@/pages/IndustryPage";
import TrackerHubPage from "@/pages/TrackerHubPage";
import TrackerCategoryPage from "@/pages/TrackerCategoryPage";
import TrackerCountryPage from "@/pages/TrackerCountryPage";
import MyTrackerPage from "@/pages/MyTrackerPage";
import TrackerUnsubscribePage from "@/pages/TrackerUnsubscribePage";
import TrackerDeletePage from "@/pages/TrackerDeletePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import MediaPage from "@/pages/MediaPage";
import AnnouncementPage from "@/pages/AnnouncementPage";
import MandatesPage from "@/pages/MandatesPage";
import MandatePage from "@/pages/MandatePage";
import SaftSolutionPage from "@/pages/SaftSolutionPage";
import SaftCountryPage from "@/pages/SaftCountryPage";
import SaftGuidePrintPage from "@/pages/SaftGuidePrintPage";
import EinvoicingSolutionPage from "@/pages/EinvoicingSolutionPage";
import EinvoicingCountryPage from "@/pages/EinvoicingCountryPage";
import EinvoicingGuidePrintPage from "@/pages/EinvoicingGuidePrintPage";
import SolutionHubPage from "@/pages/SolutionHubPage";
import SolutionCountryPage from "@/pages/SolutionCountryPage";
import SolutionGuidePrintPage from "@/pages/SolutionGuidePrintPage";
import CalculatorPage from "@/pages/CalculatorPage";

// Auth
import AuthCallback from "@/components/auth/AuthCallback";

// Components
import ChatWidget from "@/components/chat/ChatWidget";
import SEO from "@/components/SEO";
import CookieConsent from "@/components/CookieConsent";
import FloatingCTA from "@/components/common/FloatingCTA";
import ScrollToHash from "@/components/common/ScrollToHash";
import CalendarBooking from "@/components/common/CalendarBooking";
import { usePageTracking } from "@/hooks/usePageTracking";

function AppRouter() {
  const location = useLocation();
  const [bookingOpen, setBookingOpen] = useState(false);
  usePageTracking();
  // Check URL fragment for session_id (from Emergent OAuth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <SEO />
      <ScrollToHash />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/value" element={<HomePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/platform" element={<PlatformPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/evaluate" element={<EvaluatePage />} />
        <Route path="/evaluate/business-case" element={<BusinessCasePage />} />
        <Route path="/evaluate/benchmarks" element={<BenchmarksPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:hash" element={<NewsArticlePage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/sap-s4" element={<SapMigrationPage />} />
        <Route path="/sap-s4hana" element={<SapMigrationPage />} />
        <Route path="/sap-s4hana/assessment" element={<SapAssessmentPage />} />
        <Route path="/sap-s4hana/calculator" element={<SapCalculatorPage />} />
        <Route path="/sap-s4hana/whitepaper" element={<SapWhitepaperPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/imprint" element={<ImprintPage />} />
        <Route path="/resources" element={<BlogPage />} />
        <Route path="/knowledge-base" element={<SEOIndexPage />} />
        <Route path="/knowledge-base/:country" element={<SEOCountryPage />} />
        <Route path="/resources/videos" element={<VideoLibraryPage />} />
        <Route path="/resources/videos/:videoId" element={<VideoPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/media/:slug" element={<AnnouncementPage />} />
        <Route path="/mandates" element={<MandatesPage />} />
        <Route path="/mandates/:slug" element={<MandatePage />} />
        <Route path="/tracker" element={<TrackerHubPage />} />
        <Route path="/tracker/my" element={<MyTrackerPage />} />
        <Route path="/tracker/unsubscribe" element={<TrackerUnsubscribePage />} />
        <Route path="/tracker/delete" element={<TrackerDeletePage />} />
        <Route path="/tracker/:categoryId" element={<TrackerCategoryPage />} />
        <Route path="/tracker/:categoryId/:countryCode" element={<TrackerCountryPage />} />
        <Route path="/industries/:sector" element={<IndustryPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/solutions/saf-t" element={<SaftSolutionPage />} />
        <Route path="/solutions/saf-t/:country" element={<SaftCountryPage />} />
        <Route path="/print/saft/:country" element={<SaftGuidePrintPage />} />
        <Route path="/solutions/e-invoicing" element={<EinvoicingSolutionPage />} />
        <Route path="/solutions/e-invoicing/:country" element={<EinvoicingCountryPage />} />
        <Route path="/print/e-invoicing/:country" element={<EinvoicingGuidePrintPage />} />
        <Route path="/solutions/:product" element={<SolutionHubPage />} />
        <Route path="/solutions/:product/:country" element={<SolutionCountryPage />} />
        <Route path="/print/solutions/:product/:country" element={<SolutionGuidePrintPage />} />
        <Route path="/calculator" element={<CalculatorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ChatWidget />
      <CookieConsent />
      <FloatingCTA onBookDemo={() => setBookingOpen(true)} />
      <CalendarBooking isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const isRtl = i18n.language === 'ar';
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <HomepageProvider>
              <AppRouter />
            </HomepageProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
